import React, { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { number, object } from 'yup';

import Copy from 'Src/components/Copy';
import { useCheck } from 'Src/feature/check/useCheck';
import { useLogger } from 'Src/feature/context-logger/useLogger';
import { getTypedFormComponents } from 'Src/feature/form/getTypedFormComponents';
import { useForm } from 'Src/feature/form/useForm';
import { Modal } from 'Src/feature/modal/Modal';
import { ModalAction } from 'Src/feature/modal/ModalAction';
import { ModalActions } from 'Src/feature/modal/ModalActions';
import { ModalContent } from 'Src/feature/modal/ModalContent';
import { ModalHeader } from 'Src/feature/modal/ModalHeader';
import { ModalTitle } from 'Src/feature/modal/ModalTitle';
import { useModalControl } from 'Src/feature/modal/useModalControl';
import { PaymentMethodModal } from 'Src/feature/payment/PaymentMethodModal';
import { usePaymentMethod } from 'Src/feature/payment/usePaymentMethod';
import { useToast } from 'Src/feature/toasts/useToast';
import { Uuid } from 'Src/models/Uuid';
import { useError } from 'Src/network/useError';
import { sortByLabel } from 'Src/utils/sort';

import { CreateGiftCardRequest, GiftCard } from './giftCardService';

import styles from './EditGiftCardModal.module.scss';

const sectionId = 'EditGiftCardModal';

interface FormModel {
  amount: number;
}

const { Form, Field } = getTypedFormComponents<FormModel>();

interface EditGiftCardModalProps {
  open: boolean;
  giftCard?: GiftCard;
  checkId: number | undefined;
  onClose: () => void;
}

const EditGiftCardModal: FC<EditGiftCardModalProps> = ({ open, onClose, giftCard, checkId }) => {
  const { t } = useTranslation();
  const log = useLogger().getSubLogger({ name: sectionId });
  const toast = useToast();
  const { parseError } = useError();
  
  const giftCardPaymentMethods = usePaymentMethod.query.giftCardMethods();
  const giftCardPaymentMethodOptions = useMemo(() => (
    giftCardPaymentMethods.data?.map((pm) => ({ value: pm.id as number, label: pm.name })).sort(sortByLabel) ?? []
  ), [giftCardPaymentMethods.data]);
  
  const removeGiftCard = useCheck.mutate.removeGiftCard();
  const addGiftCard = useCheck.mutate.addGiftCard();
  
  const [editMode, setEditMode] = useState(false);

  const giftCardMethodModal = useModalControl();

  const form = useForm<FormModel>({
    schema: object().shape({
      amount: number().label(t('editGiftCard.labels.editAmount')).required().moreThan(0),
    }),
  });

  const handleDelete = () => {
    if (!checkId) return log.debug('handleDelete', '!checkId');
    removeGiftCard.mutate(
      { checkId, giftCardId: giftCard?.id as string },
      {
        onError: (err) => {
          const { title, message, messagePoints } = parseError(err);
          toast.make({
            id: `${sectionId}.onSave.removeGiftCard`,
            type: 'alert',
            props: { title, message, messagePoints, severity: 'error' },
          }).show();
        },
        onSuccess: onClose,
      },
    );
  };

  const onGCMethodOptionClick = (option: { value: number }) => {
    void onSave(option.value);
  };

  const handleCancelEdit = () => {
    form.action.reset();
    setEditMode(false);
  };

  const onSave = async (paymentMethodId?: number) => {
    if (!giftCard) return log.debug('onSave', '!giftCard');
    if (!checkId) return log.debug('onSave', '!checkId');
    const { isValid, values } = await form.action.submit();
    if (!isValid) return;
    if (giftCardPaymentMethodOptions.length > 1 && !paymentMethodId) {
      giftCardMethodModal.open();
      return;
    }
    giftCardMethodModal.close();
    await removeGiftCard.mutateAsync(
      { checkId, giftCardId: giftCard?.id as string },
      {
        onError: (err) => {
          const { title, message, messagePoints } = parseError(err);
          toast.make({
            id: `${sectionId}.onSave.removeGiftCard`,
            type: 'alert',
            props: { title, message, messagePoints, severity: 'error' },
          }).show();
        },
      },
    );
    const newGc = {
      account: giftCard.account,
      amount: parseFloat(values.amount.toString()),
      // NOTE: At this point, a gift card was added to the check in some fashion. We don't have an 'edit check gift card' endpoint,
      //       so we are going to do a remove and then re-add the gift card again. A problem we have though is we don't have an entry
      //       method for how that gift card got on the check in the first place. This is not on the check gift card object data
      //       at this time. So for now we will just send 'NotApplicable'. In the future, this value should be available and we
      //       can grab it off of the giftCard prop.
      cardEntryMethod: 'NotApplicable' as CreateGiftCardRequest['cardEntryMethod'],
      id: Uuid.new(),
      isReload: giftCard.isReload,
      merchantId: giftCard.merchantId,
      paymentMethodId: paymentMethodId ?? giftCardPaymentMethodOptions[0].value,
    };
    addGiftCard.mutate(
      { checkId, giftCard: newGc },
      {
        onError: (err) => {
          const { title, message, messagePoints } = parseError(err);
          toast.make({
            id: `${sectionId}.onSave.addGiftCard`,
            type: 'alert',
            props: { title, message, messagePoints, severity: 'error' },
          }).show();
        },
        onSuccess: onClose,
      },
    );
  };
  
  return (
    <>
      <Modal open={open} onBackDropClick={onClose} >
        <ModalHeader>
          <ModalTitle copy={t('editGiftCard.labels.title')} />
        </ModalHeader>
        <ModalContent>
          <div className={styles['content']}>
            {editMode ? (
              <Form usedForm={form}>
                <Field
                  type="currencyInput"
                  name="amount"
                  placeholder={giftCard?.amount.toFixed(2).toString()}
                  className={styles['currency-input']}
                />
              </Form>
            ) : <Copy type="currency" variant="h4" value={giftCard?.amount} />}
          </div>
        </ModalContent>
        <ModalActions>
          {editMode ? (
            <>
              <ModalAction color="secondary" label={t('labels.cancel')} onClick={handleCancelEdit} />
              <ModalAction color="primary" label={t('labels.save')} disabled={!form.state.isValid} onClick={() => onSave()} />
            </>
          ) : (
            <>
              <ModalAction variant="outlined" label={t('labels.delete')} onClick={handleDelete} />
              <ModalAction color="secondary" label={t('editGiftCard.labels.editAmount')} onClick={() => setEditMode(true)} />
            </>
          )}
        </ModalActions>
      </Modal>
      <PaymentMethodModal
        open={giftCardMethodModal.isOpen}
        title={t('payment.action.selectGCMethod')}
        paymentMethodOptions={giftCardPaymentMethodOptions}
        onOptionClick={onGCMethodOptionClick}
        onClose={giftCardMethodModal.close}
      />
    </>
  );
};

export { EditGiftCardModal };
